import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Drawer,
  IconButton,
  useTheme,
  Typography,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Fab,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Divider,
  Tooltip,
  Autocomplete,
  Chip,
  TextField,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  AccountBalance as AccountBalanceIcon,
} from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { GoalForm, GoalList, GoalsGraph } from "../components/goals";
import LoadingTimerDebug from "../components/common/LoadingTimerDebug";
import FloatingActionMenu from "../components/common/FloatingActionMenu";
import PageShareModal from "../components/common/PageShareModal";
import { Goal, GoalLinkedAccount, Debt, Expense } from "../lib/types";
import {
  filterGoalsByCategories,
  getGoalCategoryOptions,
} from "../lib/goalsUtils";
import { goalAPI, debtAPI, expenseAPI } from "../services/api";
import { demoService } from "../services/demo/demoService";
import { demoAPI } from "../services/api/demoApi";
import { plaidService } from "../services/external";
import { useShareAction } from "../contexts/ShareActionContext";

interface GoalsPageProps {
  setPageTitle?: (title: string) => void;
}

const GoalsPage: React.FC<GoalsPageProps> = ({ setPageTitle }) => {
  const theme = useTheme();
  const { setShareHandler } = useShareAction();
  const [searchParams, setSearchParams] = useSearchParams();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [accounts, setAccounts] = useState<GoalLinkedAccount[]>([]);
  const [debts, setDebts] = useState<Debt[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [goalToEdit, setGoalToEdit] = useState<Goal | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [isAccountsLoading, setIsAccountsLoading] = useState(false);
  const [pageShareOpen, setPageShareOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<"list" | "graph">(
    searchParams.get("view") === "graph" ? "graph" : "list"
  );

  // Link accounts dialog state
  const [isLinkAccountsDialogOpen, setIsLinkAccountsDialogOpen] =
    useState(false);
  const [goalToLink, setGoalToLink] = useState<Goal | undefined>(undefined);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);

  const getCreditTargetAmount = useCallback((account: GoalLinkedAccount) => {
    if (account.creditLimit != null && account.creditLimit > 0) {
      return account.creditLimit;
    }

    if (account.availableBalance != null) {
      return account.availableBalance + (account.balance || 0);
    }

    return account.balance || 0;
  }, []);

  const getCreditCurrentAmount = useCallback((account: GoalLinkedAccount) => {
    if (account.availableBalance != null) {
      return account.availableBalance;
    }

    if (account.creditLimit != null && account.balance != null) {
      return Math.max(account.creditLimit - account.balance, 0);
    }

    return account.balance || 0;
  }, []);

  const getCreditLinkedTargetAmount = useCallback(
    (linkedAccounts: GoalLinkedAccount[]) => {
      const creditAccounts = linkedAccounts.filter(
        (account) => account.type === "credit"
      );

      if (creditAccounts.length === 0) return null;

      const total = creditAccounts.reduce(
        (sum, account) => sum + getCreditTargetAmount(account),
        0
      );

      return total;
    },
    [getCreditTargetAmount]
  );

  // Set page title
  useEffect(() => {
    if (setPageTitle) {
      setPageTitle("Goals");
    }
  }, [setPageTitle]);

  useEffect(() => {
    const viewParam = searchParams.get("view");
    const next = viewParam === "graph" ? "graph" : "list";
    if (next !== viewMode) setViewMode(next);
  }, [searchParams, viewMode]);

  useEffect(() => {
    setShareHandler(() => {
      setPageShareOpen(true);
    });
    return () => setShareHandler(null);
  }, [setShareHandler]);

  // Load goals and accounts
  useEffect(() => {
    const loadData = async () => {
      const loadStart = performance.now();
      try {
        setLoading(true);

        const [loadedGoals, loadedDebts, loadedExpenses] = await Promise.all([
          goalAPI.getAll(),
          debtAPI.getAll(),
          expenseAPI.getAll(),
        ]);
        console.log(
          "[GoalsPage] Loaded goals from API:",
          loadedGoals.map((g) => ({
            id: g.id,
            name: g.name,
            targetAmountSource: g.targetAmountSource,
            linkedDebtIds: g.linkedDebtIds,
            linkedExpenseIds: g.linkedExpenseIds,
          }))
        );

        // Format dates properly
        const formattedGoals = loadedGoals.map((goal) => ({
          ...goal,
          targetDate:
            typeof goal.targetDate === "string"
              ? new Date(goal.targetDate)
              : goal.targetDate,
          createdAt:
            typeof goal.createdAt === "string"
              ? new Date(goal.createdAt)
              : goal.createdAt,
          updatedAt:
            typeof goal.updatedAt === "string"
              ? new Date(goal.updatedAt)
              : goal.updatedAt,
        }));

        setGoals(formattedGoals);
        setDebts(loadedDebts);
        setExpenses(loadedExpenses);
        console.debug("[GoalsPage] Base data loaded", {
          goals: formattedGoals.length,
          debts: loadedDebts.length,
          expenses: loadedExpenses.length,
          ms: Math.round(performance.now() - loadStart),
        });
        setLoading(false);

        setIsAccountsLoading(true);
        const accountsStart = performance.now();
        try {
          if (demoService.isDemo()) {
            const demoAccounts = demoAPI.getAccounts();
            const mapped = demoAccounts.map((account) => ({
              id: account.id,
              name: account.name,
              balance: account.balance ?? account.available_balance ?? 0,
              availableBalance:
                account.available_credit ??
                account.available_balance ??
                undefined,
              creditLimit: account.credit_limit ?? undefined,
              type: account.type,
              subtype: account.subtype,
              mask: account.mask,
              institutionName: account.institution,
            }));
            setAccounts(mapped);
            console.debug("[GoalsPage] Accounts loaded", {
              count: mapped.length,
              ms: Math.round(performance.now() - accountsStart),
            });
          } else {
            const plaidAccounts = await plaidService.getAccounts();
            const mapped = plaidAccounts.map((account) => ({
              id: account.account_id,
              name: account.official_name || account.name,
              balance:
                account.balances.current ?? account.balances.available ?? 0,
              availableBalance: account.balances.available ?? undefined,
              creditLimit: account.balances.limit ?? undefined,
              type: account.type,
              subtype: account.subtype,
              mask: account.mask,
              institutionName: account.institution?.name,
            }));
            setAccounts(mapped);
            console.debug("[GoalsPage] Accounts loaded", {
              count: mapped.length,
              ms: Math.round(performance.now() - accountsStart),
            });
          }
        } catch (error) {
          console.error("Failed to load accounts:", error);
        } finally {
          setIsAccountsLoading(false);
        }
      } catch (error) {
        console.error("Failed to load goals:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Subscribe to demo mode changes
    const unsubscribe = demoService.subscribe(() => {
      loadData();
    });

    return unsubscribe;
  }, []);

  // Update goal current amounts when accounts change
  useEffect(() => {
    if (accounts.length > 0 && goals.length > 0) {
      const updatedGoals = goals.map((goal) => {
        const targetSource = goal.targetAmountSource || "custom";
        if (
          targetSource === "custom" &&
          goal.linkedAccountIds &&
          goal.linkedAccountIds.length > 0
        ) {
          const linkedAccounts = accounts.filter((acc) =>
            goal.linkedAccountIds.includes(acc.id)
          );
          const newCurrentAmount = linkedAccounts.reduce((sum, acc) => {
            if (acc.type === "credit") {
              return sum + getCreditCurrentAmount(acc);
            }
            return sum + (acc.balance || 0);
          }, 0);
          const creditTargetAmount =
            getCreditLinkedTargetAmount(linkedAccounts);

          const nextGoal = { ...goal };
          let hasUpdates = false;

          if (newCurrentAmount !== goal.currentAmount) {
            nextGoal.currentAmount = newCurrentAmount;
            hasUpdates = true;
          }

          if (
            creditTargetAmount !== null &&
            creditTargetAmount !== goal.targetAmount
          ) {
            nextGoal.targetAmount = creditTargetAmount;
            hasUpdates = true;
          }

          if (hasUpdates) {
            console.log("[GoalsPage] Recalculated linked account totals:", {
              id: goal.id,
              targetAmount: nextGoal.targetAmount,
              currentAmount: nextGoal.currentAmount,
              targetAmountSource: targetSource,
            });
            return nextGoal;
          }
        }
        return goal;
      });

      const hasChanges = updatedGoals.some(
        (goal, index) =>
          goal.currentAmount !== goals[index].currentAmount ||
          goal.targetAmount !== goals[index].targetAmount
      );

      if (hasChanges) {
        setGoals(updatedGoals);
      }
    }
  }, [accounts, goals, getCreditLinkedTargetAmount, getCreditCurrentAmount]);

  useEffect(() => {
    if (goals.length === 0) return;

    const updatedGoals = goals.map((goal) => {
      if (goal.targetAmountSource === "debts" && goal.linkedDebtIds?.length) {
        const selectedDebts = debts.filter((debt) =>
          goal.linkedDebtIds?.includes(debt.id)
        );
        const targetAmount = selectedDebts.reduce(
          (sum, debt) =>
            sum + (debt.creditLimit > 0 ? debt.creditLimit : debt.balance),
          0
        );
        const currentAmount = selectedDebts.reduce((sum, debt) => {
          if (debt.creditLimit > 0) {
            return sum + Math.max(debt.creditLimit - debt.balance, 0);
          }
          return sum;
        }, 0);

        const nextGoal = { ...goal };
        let hasUpdates = false;

        if (targetAmount > 0 && targetAmount !== goal.targetAmount) {
          nextGoal.targetAmount = targetAmount;
          hasUpdates = true;
        }

        if (currentAmount !== goal.currentAmount) {
          nextGoal.currentAmount = currentAmount;
          hasUpdates = true;
        }

        if (hasUpdates) {
          console.log("[GoalsPage] Recalculated debt goal totals:", {
            id: goal.id,
            targetAmount,
            currentAmount,
            linkedDebtIds: goal.linkedDebtIds,
          });
          return nextGoal;
        }
      }

      if (
        goal.targetAmountSource === "expenses" &&
        goal.linkedExpenseIds?.length
      ) {
        const targetAmount = expenses
          .filter((expense) => goal.linkedExpenseIds?.includes(expense.id))
          .reduce((sum, expense) => sum + expense.amount, 0);

        if (targetAmount > 0 && targetAmount !== goal.targetAmount) {
          console.log("[GoalsPage] Recalculated expense goal total:", {
            id: goal.id,
            targetAmount,
            linkedExpenseIds: goal.linkedExpenseIds,
          });
          return { ...goal, targetAmount };
        }
      }

      return goal;
    });

    const hasChanges = updatedGoals.some(
      (goal, index) =>
        goal.targetAmount !== goals[index].targetAmount ||
        goal.currentAmount !== goals[index].currentAmount
    );

    if (hasChanges) {
      setGoals(updatedGoals);
    }
  }, [debts, expenses, goals]);

  const handleOpenDrawer = () => {
    setGoalToEdit(undefined);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setGoalToEdit(undefined);
  };

  const handleAddGoal = async (
    goalData: Omit<Goal, "id" | "createdAt" | "updatedAt">
  ) => {
    try {
      const newGoal = await goalAPI.add(goalData);

      // Format dates
      const formattedGoal = {
        ...newGoal,
        targetDate:
          typeof newGoal.targetDate === "string"
            ? new Date(newGoal.targetDate)
            : newGoal.targetDate,
        createdAt:
          typeof newGoal.createdAt === "string"
            ? new Date(newGoal.createdAt)
            : newGoal.createdAt,
        updatedAt:
          typeof newGoal.updatedAt === "string"
            ? new Date(newGoal.updatedAt)
            : newGoal.updatedAt,
      };

      setGoals([...goals, formattedGoal]);
      handleCloseDrawer();
    } catch (error) {
      console.error("Failed to add goal:", error);
      alert("Failed to create goal. Please try again.");
    }
  };

  const handleEditGoal = (goal: Goal) => {
    setGoalToEdit(goal);
    setIsDrawerOpen(true);
  };

  const handleUpdateGoal = async (
    goalData: Omit<Goal, "id" | "createdAt" | "updatedAt">
  ) => {
    if (!goalToEdit) return;

    try {
      const updatedGoal = await goalAPI.update({
        ...goalToEdit,
        ...goalData,
      });

      // Format dates
      const formattedGoal = {
        ...updatedGoal,
        targetDate:
          typeof updatedGoal.targetDate === "string"
            ? new Date(updatedGoal.targetDate)
            : updatedGoal.targetDate,
        createdAt:
          typeof updatedGoal.createdAt === "string"
            ? new Date(updatedGoal.createdAt)
            : updatedGoal.createdAt,
        updatedAt:
          typeof updatedGoal.updatedAt === "string"
            ? new Date(updatedGoal.updatedAt)
            : updatedGoal.updatedAt,
      };

      setGoals((prevGoals) =>
        prevGoals.map((g) => (g.id === formattedGoal.id ? formattedGoal : g))
      );
      handleCloseDrawer();
    } catch (error) {
      console.error("Failed to update goal:", error);
      alert("Failed to update goal. Please try again.");
    }
  };

  const handleOpenLinkAccounts = (goal: Goal) => {
    setGoalToLink(goal);
    setSelectedAccountIds(goal.linkedAccountIds || []);
    setIsLinkAccountsDialogOpen(true);
  };

  const handleToggleAccount = (accountId: string) => {
    setSelectedAccountIds((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId]
    );
  };

  const handleConfirmLinkAccounts = async () => {
    if (!goalToLink) return;

    try {
      // Calculate new current amount from selected accounts
      const linkedAccounts = accounts.filter((acc) =>
        selectedAccountIds.includes(acc.id)
      );
      const newCurrentAmount = linkedAccounts.reduce((sum, acc) => {
        if (acc.type === "credit") {
          return sum + getCreditCurrentAmount(acc);
        }
        return sum + (acc.balance || 0);
      }, 0);
      const creditTargetAmount = getCreditLinkedTargetAmount(linkedAccounts);

      const updatedGoal = await goalAPI.update({
        ...goalToLink,
        linkedAccountIds: selectedAccountIds,
        currentAmount:
          selectedAccountIds.length > 0
            ? newCurrentAmount
            : goalToLink.currentAmount,
        targetAmount: creditTargetAmount ?? goalToLink.targetAmount,
      });

      // Format dates
      const formattedGoal = {
        ...updatedGoal,
        targetDate:
          typeof updatedGoal.targetDate === "string"
            ? new Date(updatedGoal.targetDate)
            : updatedGoal.targetDate,
        createdAt:
          typeof updatedGoal.createdAt === "string"
            ? new Date(updatedGoal.createdAt)
            : updatedGoal.createdAt,
        updatedAt:
          typeof updatedGoal.updatedAt === "string"
            ? new Date(updatedGoal.updatedAt)
            : updatedGoal.updatedAt,
      };

      setGoals((prevGoals) =>
        prevGoals.map((g) => (g.id === formattedGoal.id ? formattedGoal : g))
      );

      setIsLinkAccountsDialogOpen(false);
      setGoalToLink(undefined);
      setSelectedAccountIds([]);
    } catch (error) {
      console.error("Failed to link accounts:", error);
      alert("Failed to link accounts. Please try again.");
    }
  };

  const categoryOptions = useMemo(() => getGoalCategoryOptions(goals), [goals]);

  const filteredGoals = useMemo(
    () => filterGoalsByCategories(goals, selectedCategories),
    [goals, selectedCategories]
  );

  return (
    <>
      {/* Main Content */}
      <Box sx={{ maxWidth: 1200, margin: "0 auto", padding: { xs: 2, md: 3 } }}>
        <Tabs
          value={viewMode}
          onChange={(_, value) => {
            const next = value === "graph" ? "graph" : "list";
            setViewMode(next);
            const params = new URLSearchParams(searchParams);
            if (next === "graph") params.set("view", "graph");
            else params.delete("view");
            setSearchParams(params);
          }}
          sx={{ mb: 2 }}
        >
          <Tab label="List" value="list" />
          <Tab label="Graph" value="graph" />
        </Tabs>

        {viewMode === "list" ? (
          <GoalList
            goals={filteredGoals}
            accounts={accounts}
            loading={loading}
            accountsLoading={isAccountsLoading}
            categoryOptions={categoryOptions}
            selectedCategories={selectedCategories}
            onSelectedCategoriesChange={setSelectedCategories}
            onEdit={handleEditGoal}
            onLinkAccounts={handleOpenLinkAccounts}
            onAddNew={handleOpenDrawer}
          />
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Autocomplete
                multiple
                freeSolo
                options={categoryOptions}
                value={selectedCategories}
                onChange={(_, value) =>
                  setSelectedCategories(value.map((v) => String(v)))
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      key={`${option}-${index}`}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter Categories"
                    placeholder="Type to search or add, press Enter"
                  />
                )}
              />
            </Box>

            <GoalsGraph
              goals={filteredGoals}
              onEditGoal={(goal) => handleEditGoal(goal)}
            />
          </>
        )}
      </Box>

      {/* Add/Edit Goal Drawer */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 450 },
            backgroundColor: theme.palette.background.paper,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {goalToEdit ? "Edit Goal" : "Create New Goal"}
          </Typography>
          <IconButton onClick={handleCloseDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>
        <GoalForm
          onSubmit={goalToEdit ? handleUpdateGoal : handleAddGoal}
          onCancel={handleCloseDrawer}
          goalToEdit={goalToEdit}
          accounts={accounts}
          debts={debts}
          expenses={expenses}
          categoryOptions={categoryOptions}
        />
      </Drawer>

      {/* Link Accounts Dialog */}
      <Dialog
        open={isLinkAccountsDialogOpen}
        onClose={() => {
          setIsLinkAccountsDialogOpen(false);
          setGoalToLink(undefined);
          setSelectedAccountIds([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBalanceIcon color="primary" />
            Link Accounts to "{goalToLink?.name}"
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select accounts to track progress for this goal. The goal's current
            amount will be automatically calculated from the total balance of
            linked accounts.
          </DialogContentText>
          {accounts.length === 0 ? (
            <Box
              sx={{
                py: 4,
                textAlign: "center",
                bgcolor: theme.palette.action.hover,
                borderRadius: 1,
              }}
            >
              <Typography color="text.secondary">
                No accounts available. Connect your accounts from the Accounts
                page to link them to goals.
              </Typography>
            </Box>
          ) : (
            <List sx={{ pt: 0 }}>
              {accounts.map((account, index) => (
                <React.Fragment key={account.id}>
                  {index > 0 && <Divider />}
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleToggleAccount(account.id)}
                      dense
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedAccountIds.includes(account.id)}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={account.name}
                        secondary={`$${account.balance.toLocaleString()}`}
                      />
                    </ListItemButton>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
          {selectedAccountIds.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: theme.palette.primary.main + "10",
                borderRadius: 1,
                border: `1px solid ${theme.palette.primary.main}30`,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Selected accounts total:
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                $
                {accounts
                  .filter((acc) => selectedAccountIds.includes(acc.id))
                  .reduce((sum, acc) => sum + (acc.balance || 0), 0)
                  .toLocaleString()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsLinkAccountsDialogOpen(false);
              setGoalToLink(undefined);
              setSelectedAccountIds([]);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLinkAccounts}
            variant="contained"
            disabled={accounts.length === 0}
          >
            {selectedAccountIds.length === 0 ? "Unlink All" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Buttons */}
      <FloatingActionMenu label="Goal actions">
        {/* Add Goal FAB */}
        <Tooltip title="Add Goal" placement="left" arrow>
          <Fab
            color="primary"
            size="large"
            aria-label="add goal"
            onClick={handleOpenDrawer}
          >
            <AddIcon />
          </Fab>
        </Tooltip>
      </FloatingActionMenu>

      <PageShareModal
        open={pageShareOpen}
        onClose={() => setPageShareOpen(false)}
        pageType="goals"
        payload={{}}
      />

      <LoadingTimerDebug
        loading={loading}
        label="Goals"
        details={{
          goals: goals.length,
          accounts: accounts.length,
          debts: debts.length,
          expenses: expenses.length,
          accountsLoading: isAccountsLoading,
          demoMode: demoService.isDemo(),
        }}
      />
    </>
  );
};

export default GoalsPage;
